'use client';

import React, { useState } from 'react';
import Image, { ImageLoaderProps } from 'next/image';

interface AppImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  fill?: boolean;
  sizes?: string;
  onClick?: () => void;
  fallbackSrc?: string;
  style?: React.CSSProperties;
}

const passthroughLoader = ({ src }: ImageLoaderProps) => src;

function AppImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  quality = 75,
  placeholder = 'empty',
  blurDataURL,
  fill = false,
  sizes,
  onClick,
  fallbackSrc = '/assets/images/no_image.png',
  style,
}: AppImageProps) {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // More reliable external URL detection
  const isExternal = imageSrc.startsWith('http://') || imageSrc.startsWith('https://');

  const handleError = () => {
    if (!hasError && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(true);
    }
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const commonClassName = `${className} ${isLoading ? 'bg-gray-200' : ''} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`;

  const unoptimized = isExternal || imageSrc.startsWith('data:');
  const loader = isExternal ? passthroughLoader : undefined;

  if (fill) {
    return (
      <div className={`relative ${className}`}>
        <Image
          loader={loader}
          unoptimized={unoptimized}
          src={imageSrc}
          alt={alt}
          className={commonClassName}
          priority={priority}
          quality={quality}
          placeholder={placeholder}
          blurDataURL={blurDataURL}
          fill
          sizes={sizes || '100vw'}
          onError={handleError}
          onLoad={handleLoad}
          onClick={onClick}
          style={{ objectFit: 'cover', ...style }}
        />
      </div>
    );
  }

  return (
    <Image
      loader={loader}
      unoptimized={unoptimized}
      src={imageSrc}
      alt={alt}
      className={commonClassName}
      priority={priority}
      quality={quality}
      placeholder={placeholder}
      blurDataURL={blurDataURL}
      width={width || 400}
      height={height || 300}
      onError={handleError}
      onLoad={handleLoad}
      onClick={onClick}
      style={style}
    />
  );
}

export default AppImage;
